// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import Order from "./order.js"
import OrderGenerationOrder from "./order-generation-order.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "file_path": {
      "column": null,
      "name": "file_path",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "OrderGeneration",
  "collectionKey": "order_generations",
  "collectionName": "order_generations",
  "i18nKey": "order_generation",
  "camelizedLower": "orderGeneration",
  "name": "OrderGeneration",
  "nameDasherized": "order-generation",
  "pluralName": "order_generations",
  "ransackable_associations": [
    {
      "className": "ActiveStorage::Attachment",
      "collectionName": "active_storage_attachments",
      "foreignKey": "record_id",
      "name": "file_attachment",
      "macro": "has_one",
      "resource_name": "ActiveStorageAttachment",
      "through": null
    },
    {
      "className": "ActiveStorage::Blob",
      "collectionName": "active_storage_blobs",
      "foreignKey": "blob_id",
      "name": "file_blob",
      "macro": "has_one",
      "resource_name": "ActiveStorageBlob",
      "through": "file_attachment"
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "OrderGenerationOrder",
      "collectionName": "order_generation_orders",
      "foreignKey": "order_generation_id",
      "name": "order_generation_orders",
      "macro": "has_many",
      "resource_name": "OrderGenerationOrder",
      "through": null
    },
    {
      "className": "Order",
      "collectionName": "orders",
      "foreignKey": "order_id",
      "name": "orders",
      "macro": "has_many",
      "resource_name": "Order",
      "through": "order_generation_orders"
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "OrderGenerationOrder",
      "collectionName": "order_generation_orders",
      "foreignKey": "order_generation_id",
      "name": "order_generation_orders",
      "macro": "has_many",
      "resource_name": "OrderGenerationOrder",
      "through": null
    },
    {
      "className": "Order",
      "collectionName": "orders",
      "foreignKey": "order_id",
      "name": "orders",
      "macro": "has_many",
      "resource_name": "Order",
      "through": "order_generation_orders"
    }
  ],
  "paramKey": "order_generation",
  "primaryKey": "id"
}

/** Frontend model for OrderGeneration. */
export default class OrderGeneration extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  filePath() {
    return this.readAttributeUnderscore("file_path")
  }

  /** @returns {boolean} */
  hasFilePath() {
    const value = this.filePath()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./order-generation-order.js").default>} */
  orderGenerationOrders() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["order_generation_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "order_generation_orders",
        model: this,
        modelClass: OrderGenerationOrder
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./order-generation-order.js").default>>} */
  loadOrderGenerationOrders() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["order_generation_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "order_generation_orders",
        model: this,
        modelClass: OrderGenerationOrder
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./order-generation-order.js").default>>} */
  ensureOrderGenerationOrdersLoaded() {
    return this.ensureAssociationLoaded("order_generation_orders")
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./order.js").default>} */
  orders() {
    return new Collection(
      {
        reflectionName: "orders",
        model: this,
        modelClass: Order
      },
      {
        params: {
          through: {
            model: "OrderGeneration",
            id: this.primaryKey(),
            reflection: "orders"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./order.js").default>>} */
  loadOrders() {
    return this._loadHasManyReflection(
      {
        reflectionName: "orders",
        model: this,
        modelClass: Order
      },
      {
        params: {
          through: {
            model: "OrderGeneration",
            id: this.primaryKey(),
            reflection: "orders"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./order.js").default>>} */
  ensureOrdersLoaded() {
    return this.ensureAssociationLoaded("orders")
  }
}
